import React, { useEffect, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ShieldExclamationIcon } from '@heroicons/react/24/outline';

import { SUPPORT_ENDED_PATH } from 'router/pathName';
import { ConfirmDialog, ErrorNotice, primaryButton } from 'components/Operator/OperatorUi';
import {
  getSupportProfile,
  listCompanySupportAccesses,
  readSupportAccess,
  SupportClaims,
  supportClaims,
  supportDeadline,
  supportNavigation,
} from './supportApi';
import { SignInAgain, useSupportDate, useSupportReturn } from './supportUi';

/** setTimeout に渡せる最大の待ち時間（約24.8日）。 */
const MAX_TIMEOUT_MS = 2 ** 31 - 1;

/** 帯の右のボタン（「サポートを終了する」）。白地に赤（red-700、白に対して 6.5）。 */
const endButton =
  'inline-flex h-10 items-center justify-center rounded-md border border-red-700 bg-white px-4 text-subtitle-2 text-red-700 disabled:opacity-60';

/**
 * YAC-2026-009 PR7b。サポート中の帯。管理者の画面（Layout）の上に、常に出す（サポートのセッションのときだけ）。
 * 「運営のサポート中（○○ 株式会社、期限 ○日 ○時まで）」と、「サポートを終了する」。
 * 2026-11-03 UAT A17（和田様）。左右の2つのボタン（「運営の画面に戻る」＝サポートは続く、「戻ってサポートを終わらせる」）の
 * 違いが分からないため、帯のボタンは「サポートを終了する」（確認の後に終わらせて運営の画面に戻る）の1つにした。
 * サポートを続けたまま戻るのは、左下のプロフィールの「運営の画面に戻る」（ログアウトの代わり）で行える。
 *
 * 期限は、サポートの期限（expires_at）と運営のログインの締め切り（access の otp_deadline）の早い方。
 * 会社名と expires_at は、入ったときの応答の記録（readSupportAccess）から読む。無ければ（別のブラウザの保存が使えない
 * など）サーバーから読む。
 * - サポートの期限（expires_at）が先に来たら、帯を「サポートの期限が切れました。運営の画面に戻ってください」にし、
 *   「運営の画面に戻る」だけを出す（#393 の Codex の P2 の直し: 期限の後も、運営のログインの締め切りまでは戻れる）。
 * - 運営のログインの締め切り（otp_deadline）が先に来たら、もう戻れないので、終わりの画面へ移す（そこで「戻る」を
 *   押すと 401 operator_sign_in_required で運営のログインの画面へ）。
 */
const SupportBanner: React.FC = () => {
  const claims = supportClaims();
  if (!claims) return null;
  return <Banner claims={claims} />;
};

const Banner: React.FC<{ claims: SupportClaims }> = ({ claims }) => {
  const { t } = useTranslation('layout');
  const formatDate = useSupportDate();
  const labelId = useId();
  const { busy, error, signIn, run } = useSupportReturn();
  const [confirming, setConfirming] = useState(false);
  const stored = readSupportAccess();
  const known = stored && stored.id === claims.accessId ? stored : null;
  const [company, setCompany] = useState<string | null>(known?.company.name ?? null);
  const [expiresAt, setExpiresAt] = useState<string | null>(known?.expires_at ?? null);

  useEffect(() => {
    if (known) return;
    let active = true;
    // 入ったときの記録が無い。会社名はプロフィール、期限はお客様の側の一覧（自分のサポート）から読む。
    getSupportProfile()
      .then((profile) => {
        if (active) setCompany(profile.company);
      })
      .catch((failure) => console.error('Failed to fetch the support company', failure));
    listCompanySupportAccesses()
      .then((rows) => {
        const mine = rows.find((row) => row.id === claims.accessId);
        if (active && mine) setExpiresAt(mine.expires_at);
      })
      .catch((failure) => console.error('Failed to fetch the support expiry', failure));
    return () => {
      active = false;
    };
    // claims.accessId が替わるのは、セッションが替わったとき（開き直す）だけ。
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [claims.accessId]);

  const deadline = supportDeadline(expiresAt, claims);
  // 期限の後も「戻る」が使えるか（サポートの期限が、運営のログインの締め切りより前に来る）。締め切りと同時か
  // 締め切りが先なら、もう戻れない（終わりの画面へ。そこで戻ると 401 operator_sign_in_required でログインへ）。
  const supportFirst = deadline !== null && claims.otpDeadline !== null && deadline < claims.otpDeadline;
  const [expired, setExpired] = useState(false);

  useEffect(() => {
    if (deadline === null) return;
    const wait = deadline - Date.now();
    const timer = setTimeout(() => {
      if (supportFirst) setExpired(true);
      else supportNavigation.assign(SUPPORT_ENDED_PATH);
    }, Math.min(Math.max(wait, 0), MAX_TIMEOUT_MS));
    return () => clearTimeout(timer);
  }, [deadline, supportFirst]);

  // サポートの期限の後で、運営のログインの締め切りまでの間。
  useEffect(() => {
    if (!expired || claims.otpDeadline === null) return;
    const wait = claims.otpDeadline - Date.now();
    const timer = setTimeout(() => supportNavigation.assign(SUPPORT_ENDED_PATH), Math.min(Math.max(wait, 0), MAX_TIMEOUT_MS));
    return () => clearTimeout(timer);
  }, [expired, claims.otpDeadline]);

  const name = company ?? '';
  const text = expired
    ? t('support.band-expired')
    : deadline !== null
    ? t('support.band', { company: name, date: formatDate(deadline) })
    : t('support.band-no-deadline', { company: name });

  return (
    <section
      aria-labelledby={labelId}
      data-support-banner
      className="sticky top-0 z-30 flex flex-col gap-2 border-b-2 border-amber-600 bg-amber-50 px-4 py-2 text-amber-900"
    >
      <div className="flex flex-wrap items-center gap-3">
        <ShieldExclamationIcon className="h-6 w-6 shrink-0" aria-hidden="true" />
        <div className="min-w-0 flex-1">
          <p id={labelId} className="text-subtitle-2 font-bold">
            {text}
          </p>
          <p className="text-caption">{t('support.band-note')}</p>
        </div>
        {/* 期限の後は「戻る」だけ（記録はサーバーが期限で閉じる）。それまでは「サポートを終了する」だけ（UAT A17）。 */}
        {expired ? (
          <button type="button" className={primaryButton} onClick={() => run(false)} disabled={busy}>
            {busy ? t('support.returning') : t('support.return')}
          </button>
        ) : (
          <button type="button" className={endButton} onClick={() => setConfirming(true)} disabled={busy}>
            {t('support.return-and-end')}
          </button>
        )}
      </div>
      {!confirming ? <ErrorNotice messages={error ? [error] : []} /> : null}
      {!confirming && signIn ? <SignInAgain /> : null}
      {confirming && !expired ? (
        <ConfirmDialog
          title={t('support.return-and-end-title')}
          body={
            <>
              <p>{t('support.return-and-end-confirm')}</p>
              {signIn ? <SignInAgain /> : null}
            </>
          }
          confirmLabel={busy ? t('support.returning') : t('support.return-and-end')}
          busy={busy}
          errors={error ? [error] : []}
          danger
          onClose={() => setConfirming(false)}
          onConfirm={() => run(true)}
        />
      ) : null}
    </section>
  );
};

export default SupportBanner;
